import { Box, Icons, Stack, Text, Time, Tooltip } from "@sembark-travel/ui-base"
import { TWhatsAppMessage } from "../store"
import { formatWhatsAppStatusLabel, getMessageDeliveryStatus } from "./utils"

// The delivery tick (sent / delivered / read / failed) for an outbound message,
// with the full status history on hover.
export function WhatsAppDeliveryIndicator({
	message,
}: {
	message: TWhatsAppMessage
}) {
	const status = getMessageDeliveryStatus(message)
	// The current state and when it happened -- the full history lives in the
	// message details dialog rather than crowding a hover.
	const latest = message.statuses?.[0]
	const tooltipContent = (
		<Stack gap="1">
			<Text fontWeight="semibold">{formatWhatsAppStatusLabel(status)}</Text>
			{latest?.created_at ? (
				<Text color="muted">
					<Time timestamp={latest.created_at} dateFormat timeFormat />
				</Text>
			) : null}
			{message.error?.title ? (
				<Text color="danger">
					{message.error.details || message.error.title}
				</Text>
			) : null}
		</Stack>
	)

	return (
		<Tooltip content={tooltipContent}>
			<Box display="inlineFlex" alignItems="center">
				<WhatsAppStatusIcon status={status} />
			</Box>
		</Tooltip>
	)
}

/**
 * The tick for one delivery state. A component rather than a helper so this
 * module keeps exporting only components, which is what lets Fast Refresh
 * preserve state here.
 */
export function WhatsAppStatusIcon({
	status,
}: {
	status: TWhatsAppMessage["status"]
}) {
	if (status === "failed") {
		return <Icons.AttentionSolid color="danger" size="4" />
	}
	if (status === "accepted") {
		return <Icons.Clock size="4" opacity="50" />
	}
	if (status === "sent") {
		return <Icons.Ok size="4" />
	}
	if (status === "read") {
		return <Icons.CheckCheck size="4" color="accent" />
	}

	return <Icons.CheckCheck size="4" />
}
